<template>
    <div class="custom-field-container">
        <div class="margin-bottom-small">
            <g-typography :value="label" />
            <g-typography v-if="description && isDescriptionUnderLabel" color="secondary" class="custom-field-description" :value="description" />
        </div>
        <keep-alive>
            <component
                :is="componentName"
                :value="value"
                :class="classes"
                :placeholder="placeholder"
                :options="options"
                :checked="isChecked"
                v-bind="attributes"
                :inputReadOnly="isInputReadOnly"
                :disabled="disabled"
                @change="changeHandler"
            >
                <template v-if="isDropdown">
                    <template v-for="option in field.options">
                        <g-select-option :value="option.value">{{ option.label }}</g-select-option>
                    </template>
                </template>
            </component>
        </keep-alive>
        <g-typography v-if="description && !isDescriptionUnderLabel" color="secondary" class="custom-field-description" :value="description" />
    </div>
</template>

<script>
    import {
        FIELD_COMPONENT_MAP,
        FIELD_DATE_PICKER,
        FIELD_DROPDOWN_SELECT,
        FIELD_MULTI_LINE_TEXT,
        FIELD_MULTIPLE_CHECKBOXES,
        FIELD_NUMBER,
        FIELD_RADIO_SELECT,
        FIELD_SINGLE_CHECKBOX,
    } from '../../../../constants/custom-field-types';

    export default {
        name:  'CustomFieldInput',
        props: {
            field: {
                type:     Object,
                required: true,
            },
            disabled: {
                type:    Boolean,
                default: false,
            },
        },
        data() {
            return {
                value: undefined,
            };
        },
        computed: {
            componentName() {
                return FIELD_COMPONENT_MAP.find(record => record.field === this.field.type).component;
            },
            label() {
                return this.field.external_label ? this.field.external_label : this.field.label;
            },
            options() {
                if (this.isCheckboxGroup || this.isRadioSelect) {
                    return this.field.options.map(option => {
                        return {
                            label: option.label,
                            value: option.value,
                        };
                    });
                }

                return undefined;
            },
            description() {
                return this.field.description;
            },
            classes() {
                return {
                    textarea:         this.isTextArea,
                    number:           this.isNumber,
                    dropdown:         this.isDropdown,
                    datepicker:       this.isDatePicker,
                    checkbox:         this.isCheckbox,
                    'checkbox-group': this.isCheckboxGroup,
                    'radio-select':   this.isRadioSelect,
                };
            },
            attributes() {
                const props = FIELD_COMPONENT_MAP.find(record => record.field === this.field.type).props;

                return props ? props : {};
            },
            placeholder() {
                if (this.isDropdown) {
                    return 'Select';
                }

                if (this.isDatePicker) {
                    return 'Select date';
                }

                return '';
            },
            isTextArea() {
                return this.field.type === FIELD_MULTI_LINE_TEXT;
            },
            isNumber() {
                return this.field.type === FIELD_NUMBER;
            },
            isDropdown() {
                return this.field.type === FIELD_DROPDOWN_SELECT;
            },
            isDatePicker() {
                return this.field.type === FIELD_DATE_PICKER;
            },
            isCheckbox() {
                return this.field.type === FIELD_SINGLE_CHECKBOX;
            },
            isChecked() {
                return this.isCheckbox ? !!this.value : undefined;
            },
            isCheckboxGroup() {
                return this.field.type === FIELD_MULTIPLE_CHECKBOXES;
            },
            isRadioSelect() {
                return this.field.type === FIELD_RADIO_SELECT;
            },
            isDescriptionUnderLabel() {
                return this.isRadioSelect || this.isCheckbox || this.isCheckboxGroup;
            },
            isInputReadOnly() {
                return this.isDatePicker ? true : undefined;
            },
        },
        methods: {
            changeHandler(input) {
                if (this.isNumber) {
                    if ((!isNaN(parseFloat(input)) && isFinite(input)) || input === '' || input === '-') {
                        if (input.replace(/\D/g,'').length <= 10) {
                            this.value = input;
                        }
                    }
                } else if (this.isTextArea) {
                    this.value = input.target.value;
                } else if (this.isCheckbox) {
                    this.value = input.target.checked;
                } else if (this.isRadioSelect) {
                    this.value = input.target.value;
                } else {
                    this.value = input;
                }

                this.emitValue(this.value);
            },
            emitValue(value) {
                this.$emit('input-value', {field_id: this.field.id, value: value ? value : null});
            },
        },
        created() {
            if (this.field.value !== null) {
                if (this.isCheckbox) {
                    this.value = !!this.field.value;
                } else if (this.isCheckboxGroup) {
                    this.value = this.field.value.split(', ').map(val => val.toString().trim());
                } else {
                    this.value = this.field.value;
                }

                this.emitValue(this.value);
            }
        },
    };
</script>

<style lang="scss" scoped>
    .custom-field-container {
        .margin-bottom-small {
            margin-bottom: $g-spacing-3;
        }
        & ::v-deep span.custom-field-description {
            font-size: $g-font-size-small;
            font-style: italic;
            margin: 0;
            padding-left: $g-spacing-3;
            display: block;
        }
        & .textarea {
            min-height: 100px;
        }
        & .number {
            text-align: left;
        }
        & .dropdown {
            width: 100%;
        }
        & .datepicker {
            width: 100%;
        }
        & .checkbox {
            width: 100%;
        }
        & .checkbox-group {
            display: block;
            & ::v-deep label.ant-checkbox-wrapper {
                display: block;
            }
        }
        & .radio-select {
            display: block;
            & ::v-deep label.ant-radio-wrapper {
                display: block;
            }
        }
    }
</style>
